import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { loadAttachmentArrayBuffer } from '../../utils/fileUtils';

const SHAPE_TOOLS = new Set(['rect', 'square', 'circle']);

const TOOLS = [
  { id: 'highlight', label: 'Highlight' },
  { id: 'pen', label: 'Pen' },
  { id: 'text', label: 'Text' },
  { id: 'arrowText', label: 'Arrow text' },
  { id: 'rect', label: 'Rectangle' },
  { id: 'square', label: 'Square' },
  { id: 'circle', label: 'Circle' },
  { id: 'crop', label: 'Crop' },
];

const TEXT_TOOLS = new Set(['text', 'arrowText']);

const TEXT_COLORS = [
  { id: 'yellow', value: '#facc15', label: 'Yellow' },
  { id: 'white', value: '#ffffff', label: 'White' },
  { id: 'red', value: '#ef4444', label: 'Red' },
  { id: 'orange', value: '#f97316', label: 'Orange' },
  { id: 'blue', value: '#3b82f6', label: 'Blue' },
  { id: 'green', value: '#22c55e', label: 'Green' },
  { id: 'black', value: '#111827', label: 'Black' },
];

const TEXT_SIZES = [
  { id: 'small', label: 'Small', scale: 0.012, min: 10 },
  { id: 'medium', label: 'Medium', scale: 0.018, min: 14 },
  { id: 'big', label: 'Big', scale: 0.03, min: 22 },
];

const resolveTextPx = (canvasWidth, sizeId) => {
  const preset = TEXT_SIZES.find((item) => item.id === sizeId) || TEXT_SIZES[1];
  return Math.max(preset.min, canvasWidth * preset.scale);
};

const ToolIcon = ({ id, className = 'w-4 h-4' }) => {
  switch (id) {
    case 'highlight':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      );
    case 'pen':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20l4.5-1.5L19 8.5 15.5 5 5 15.5 3.5 20H4z" />
        </svg>
      );
    case 'text':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7V5h16v2M12 5v14m-4 0h8" />
        </svg>
      );
    case 'arrowText':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h7M7.5 5v8" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9h4l4 5-4 5h-1.5" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 14H9.5" />
        </svg>
      );
    case 'rect':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="6" width="18" height="12" rx="1.5" strokeWidth={2} />
        </svg>
      );
    case 'square':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="5" y="5" width="14" height="14" rx="1.5" strokeWidth={2} />
        </svg>
      );
    case 'circle':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="7.5" strokeWidth={2} />
        </svg>
      );
    case 'crop':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 3v15a2 2 0 002 2h15M3 6h15a2 2 0 012 2v15" />
        </svg>
      );
    case 'rotate90':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 4.5A8 8 0 118 20" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 4.5V9H12" />
          <rect x="7.5" y="10.5" width="6" height="6" rx="0.75" strokeWidth={2} />
        </svg>
      );
    case 'flipHorizontal':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          {/* Vertical mirror axis */}
          <path strokeLinecap="round" strokeWidth={2} strokeDasharray="2.5 2.5" d="M12 3v18" />
          {/* Left triangle (solid fill) */}
          <path
            fill="currentColor"
            stroke="none"
            d="M4.5 6.5v11L11 12 4.5 6.5z"
          />
          {/* Right triangle (outline, mirrored) */}
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19.5 6.5v11L13 12l6.5-5.5z"
          />
        </svg>
      );
    case 'undo':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l-4-4 4-4" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10h8a5 5 0 015 5v1" />
        </svg>
      );
    case 'redo':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 14l4-4-4-4" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 10H11a5 5 0 00-5 5v1" />
        </svg>
      );
    default:
      return null;
  }
};

const drawImageToCanvas = (canvas, image) => {
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0);
};

const canvasToFile = (canvas, fileName) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not export image'));
        return;
      }
      const base = (fileName || 'image').replace(/\.[^.]+$/, '');
      resolve(new File([blob], `${base}-edited.png`, { type: 'image/png' }));
    }, 'image/png');
  });

const normalizeShapeBounds = (tool, start, point) => {
  let x = Math.min(start.x, point.x);
  let y = Math.min(start.y, point.y);
  let w = Math.abs(point.x - start.x);
  let h = Math.abs(point.y - start.y);
  if (tool === 'square' || tool === 'circle') {
    const size = Math.max(w, h);
    if (point.x < start.x) x = start.x - size;
    if (point.y < start.y) y = start.y - size;
    w = size;
    h = size;
  }
  return { x, y, w, h };
};

const pickLabelOffset = (canvas, target) => {
  const offsetX = Math.max(70, canvas.width * 0.08);
  const offsetY = Math.max(40, canvas.height * 0.05);
  let labelX = target.x + offsetX;
  let labelY = target.y - offsetY;
  if (labelX > canvas.width * 0.72) labelX = target.x - offsetX;
  if (labelY < canvas.height * 0.08) labelY = target.y + offsetY;
  return {
    labelX: Math.max(12, Math.min(canvas.width - 12, labelX)),
    labelY: Math.max(18, Math.min(canvas.height - 12, labelY)),
  };
};

const drawArrowHead = (ctx, fromX, fromY, toX, toY, size) => {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(
    toX - size * Math.cos(angle - Math.PI / 6),
    toY - size * Math.sin(angle - Math.PI / 6)
  );
  ctx.lineTo(
    toX - size * Math.cos(angle + Math.PI / 6),
    toY - size * Math.sin(angle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();
};

const ImageAnnotatorModal = ({ attachment, onClose, onSave, saving = false }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const dragStartRef = useRef(null);
  const dragRectRef = useRef(null);
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const [tool, setTool] = useState('highlight');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragRect, setDragRect] = useState(null);
  const [textDraft, setTextDraft] = useState(null);
  const [textColor, setTextColor] = useState(TEXT_COLORS[0].value);
  const [textSize, setTextSize] = useState('medium');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateDragRect = (next) => {
    dragRectRef.current = next;
    setDragRect(next);
  };

  const syncHistoryFlags = useCallback(() => {
    setCanUndo(undoRef.current.length > 0);
    setCanRedo(redoRef.current.length > 0);
  }, []);

  const snapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    undoRef.current.push(canvas.toDataURL('image/png'));
    if (undoRef.current.length > 20) undoRef.current.shift();
    redoRef.current = [];
    syncHistoryFlags();
  }, [syncHistoryFlags]);

  const restoreCanvasFromDataUrl = useCallback((dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas || !dataUrl) return;
    const image = new Image();
    image.onload = () => drawImageToCanvas(canvas, image);
    image.src = dataUrl;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const buffer = await loadAttachmentArrayBuffer(attachment);
        objectUrl = URL.createObjectURL(new Blob([buffer]));
        const image = new Image();
        image.onload = () => {
          if (cancelled) return;
          const canvas = canvasRef.current;
          if (!canvas) return;
          drawImageToCanvas(canvas, image);
          undoRef.current = [];
          redoRef.current = [];
          syncHistoryFlags();
          setLoading(false);
        };
        image.onerror = () => {
          if (!cancelled) {
            setError('Could not load image for editing');
            setLoading(false);
          }
        };
        image.src = objectUrl;
      } catch {
        if (!cancelled) {
          setError('Could not load image for editing');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  // Reload whenever the underlying image changes (including after a prior edit).
  }, [attachment?.fileUrl, attachment?.file, attachment?.localFileId, attachment?.id]);

  const canvasPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches ? event.touches[0] : event;
    return {
      x: ((source.clientX - rect.left) / rect.width) * canvas.width,
      y: ((source.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const strokeAt = (from, to) => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (tool === 'highlight') {
      ctx.strokeStyle = 'rgba(250, 204, 21, 0.45)';
      ctx.lineWidth = Math.max(18, canvasRef.current.width * 0.012);
    } else {
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = Math.max(4, canvasRef.current.width * 0.004);
    }
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  };

  const drawShapeOnCanvas = (bounds, shapeTool) => {
    const canvas = canvasRef.current;
    if (!canvas || !bounds || bounds.w < 4 || bounds.h < 4) return;
    snapshot();
    const ctx = canvas.getContext('2d');
    const lineWidth = Math.max(3, canvas.width * 0.0035);
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = lineWidth;
    ctx.lineJoin = 'round';
    if (shapeTool === 'circle') {
      ctx.beginPath();
      ctx.ellipse(
        bounds.x + bounds.w / 2,
        bounds.y + bounds.h / 2,
        bounds.w / 2,
        bounds.h / 2,
        0,
        0,
        Math.PI * 2
      );
      ctx.stroke();
      return;
    }
    ctx.strokeRect(bounds.x, bounds.y, bounds.w, bounds.h);
  };

  const handlePointerDown = (event) => {
    if (loading || error || saving) return;
    event.preventDefault();
    const point = canvasPoint(event);
    if (TEXT_TOOLS.has(tool)) {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const scaleX = rect.width / canvas.width;
      const scaleY = rect.height / canvas.height;
      if (tool === 'arrowText') {
        const { labelX, labelY } = pickLabelOffset(canvas, point);
        setTextDraft({
          mode: 'arrowText',
          targetX: point.x,
          targetY: point.y,
          labelX,
          labelY,
          left: labelX * scaleX,
          top: labelY * scaleY,
          value: '',
          color: textColor,
          size: textSize,
        });
      } else {
        setTextDraft({
          mode: 'text',
          labelX: point.x,
          labelY: point.y,
          left: point.x * scaleX,
          top: point.y * scaleY,
          value: '',
          color: textColor,
          size: textSize,
        });
      }
      return;
    }
    if (tool === 'crop' || SHAPE_TOOLS.has(tool)) {
      dragStartRef.current = point;
      updateDragRect({ x: point.x, y: point.y, w: 0, h: 0 });
      drawingRef.current = true;
      return;
    }
    snapshot();
    drawingRef.current = true;
    lastPointRef.current = point;
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const point = canvasPoint(event);
    if ((tool === 'crop' || SHAPE_TOOLS.has(tool)) && dragStartRef.current) {
      updateDragRect(normalizeShapeBounds(tool, dragStartRef.current, point));
      return;
    }
    if ((tool === 'pen' || tool === 'highlight') && lastPointRef.current) {
      strokeAt(lastPointRef.current, point);
      lastPointRef.current = point;
    }
  };

  const handlePointerUp = () => {
    const activeRect = dragRectRef.current;
    if (SHAPE_TOOLS.has(tool) && activeRect && activeRect.w >= 4 && activeRect.h >= 4) {
      drawShapeOnCanvas(activeRect, tool);
      updateDragRect(null);
    }
    drawingRef.current = false;
    lastPointRef.current = null;
    dragStartRef.current = null;
  };

  const applyCrop = () => {
    const canvas = canvasRef.current;
    const activeRect = dragRectRef.current;
    if (!canvas || !activeRect || activeRect.w < 8 || activeRect.h < 8) return;
    snapshot();
    const x = Math.max(0, Math.round(activeRect.x));
    const y = Math.max(0, Math.round(activeRect.y));
    const w = Math.min(canvas.width - x, Math.round(activeRect.w));
    const h = Math.min(canvas.height - y, Math.round(activeRect.h));
    const imageData = canvas.getContext('2d').getImageData(x, y, w, h);
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').putImageData(imageData, 0, 0);
    updateDragRect(null);
  };

  const updateTextStyle = (patch) => {
    if (patch.color) setTextColor(patch.color);
    if (patch.size) setTextSize(patch.size);
    setTextDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const commitText = () => {
    if (!textDraft?.value?.trim()) {
      setTextDraft(null);
      return;
    }
    snapshot();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const lines = textDraft.value.replace(/\r\n/g, '\n').split('\n').map((line) => line.trimEnd());
    const nonEmptyLines = lines.filter((line, idx, arr) => line.length > 0 || (idx > 0 && idx < arr.length - 1));
    const textLines = nonEmptyLines.length ? nonEmptyLines : [textDraft.value.trim()];
    const fillColor = textDraft.color || textColor || TEXT_COLORS[0].value;
    const size = resolveTextPx(canvas.width, textDraft.size || textSize);
    const lineGap = size * 1.25;
    const labelX = textDraft.labelX;
    const labelY = textDraft.labelY;

    ctx.font = `600 ${size}px sans-serif`;
    const metricsWidth = Math.max(...textLines.map((line) => ctx.measureText(line || ' ').width));

    if (textDraft.mode === 'arrowText') {
      const lineWidth = Math.max(2, canvas.width * 0.0025);
      const arrowSize = Math.max(10, canvas.width * 0.012);
      const targetX = textDraft.targetX;
      const targetY = textDraft.targetY;

      const padX = Math.max(6, size * 0.35);
      const padY = Math.max(4, size * 0.3);
      const boxW = metricsWidth + padX * 2;
      const boxH = textLines.length * lineGap + padY * 2 - (lineGap - size);
      const boxX = labelX - padX;
      const boxY = labelY - size - padY * 0.2;

      const boxCx = boxX + boxW / 2;
      const boxCy = boxY + boxH / 2;
      const dx = targetX - boxCx;
      const dy = targetY - boxCy;
      let startX = boxCx;
      let startY = boxCy;
      if (Math.abs(dx) > Math.abs(dy)) {
        startX = dx > 0 ? boxX + boxW : boxX;
        startY = boxCy;
      } else {
        startX = boxCx;
        startY = dy > 0 ? boxY + boxH : boxY;
      }

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = fillColor;
      ctx.fillStyle = fillColor;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(targetX, targetY);
      ctx.stroke();
      drawArrowHead(ctx, startX, startY, targetX, targetY, arrowSize);

      ctx.fillStyle = 'rgba(0,0,0,0.62)';
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(boxX, boxY, boxW, boxH, Math.max(3, size * 0.2));
      } else {
        ctx.rect(boxX, boxY, boxW, boxH);
      }
      ctx.fill();

      ctx.fillStyle = fillColor;
      textLines.forEach((line, index) => {
        ctx.fillText(line, labelX, labelY + index * lineGap);
      });
    } else {
      ctx.fillStyle = fillColor;
      textLines.forEach((line, index) => {
        ctx.fillText(line, labelX, labelY + index * lineGap);
      });
    }
    setTextDraft(null);
  };

  const undo = () => {
    const canvas = canvasRef.current;
    const prev = undoRef.current.pop();
    if (!prev || !canvas) {
      syncHistoryFlags();
      return;
    }
    redoRef.current.push(canvas.toDataURL('image/png'));
    if (redoRef.current.length > 20) redoRef.current.shift();
    restoreCanvasFromDataUrl(prev);
    syncHistoryFlags();
    setTextDraft(null);
    updateDragRect(null);
  };

  const redo = () => {
    const canvas = canvasRef.current;
    const next = redoRef.current.pop();
    if (!next || !canvas) {
      syncHistoryFlags();
      return;
    }
    undoRef.current.push(canvas.toDataURL('image/png'));
    if (undoRef.current.length > 20) undoRef.current.shift();
    restoreCanvasFromDataUrl(next);
    syncHistoryFlags();
    setTextDraft(null);
    updateDragRect(null);
  };

  const rotateCanvas = (degrees) => {
    const canvas = canvasRef.current;
    if (!canvas || loading || error || saving) return;
    snapshot();
    const source = document.createElement('canvas');
    source.width = canvas.width;
    source.height = canvas.height;
    source.getContext('2d').drawImage(canvas, 0, 0);

    const swap = degrees === 90 || degrees === 270;
    canvas.width = swap ? source.height : source.width;
    canvas.height = swap ? source.width : source.height;
    const ctx = canvas.getContext('2d');
    ctx.save();
    if (degrees === 90) {
      ctx.translate(canvas.width, 0);
      ctx.rotate(Math.PI / 2);
    } else if (degrees === 270) {
      ctx.translate(0, canvas.height);
      ctx.rotate(-Math.PI / 2);
    }
    ctx.drawImage(source, 0, 0);
    ctx.restore();
    setTextDraft(null);
    updateDragRect(null);
  };

  const flipHorizontal = () => {
    const canvas = canvasRef.current;
    if (!canvas || loading || error || saving) return;
    snapshot();
    const source = document.createElement('canvas');
    source.width = canvas.width;
    source.height = canvas.height;
    source.getContext('2d').drawImage(canvas, 0, 0);

    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(source, 0, 0);
    ctx.restore();
    setTextDraft(null);
    updateDragRect(null);
  };

  const handleSave = async () => {
    if (textDraft?.value?.trim()) commitText();
    const file = await canvasToFile(canvasRef.current, attachment?.fileName);
    await onSave(file);
  };

  const hintByTool = {
    highlight: 'Drag to highlight',
    pen: 'Draw with pen',
    text: 'Click, type note, Enter to add',
    arrowText: 'Click a point, type note — arrow points there',
    rect: 'Drag a rectangle',
    square: 'Drag a square',
    circle: 'Drag a circle',
    crop: 'Drag, then apply crop',
  };

  const previewBorderClass = tool === 'crop'
    ? 'border-amber-300 bg-amber-300/10'
    : 'border-red-400 bg-red-400/10';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1200] flex flex-col bg-black/80" role="dialog" aria-modal="true" aria-label="Edit image">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 shrink-0">
        <p className="text-white text-sm font-medium truncate">Edit image</p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-2 py-1.5 text-xs font-medium text-white/80 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || error || saving}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-white/10 shrink-0">
        {TOOLS.map((item) => {
          const active = tool === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setTool(item.id);
                setTextDraft(null);
                updateDragRect(null);
              }}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                active
                  ? 'bg-white text-gray-900'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
              aria-label={item.label}
              title={item.label}
              aria-pressed={active}
            >
              <ToolIcon id={item.id} />
            </button>
          );
        })}
        {tool === 'crop' && (
          <button
            type="button"
            onClick={applyCrop}
            disabled={!dragRect || dragRect.w < 8 || dragRect.h < 8}
            className="ml-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-amber-400 text-gray-900 disabled:opacity-40"
          >
            Apply
          </button>
        )}
        <div className="w-px h-5 bg-white/15 mx-0.5" aria-hidden />
        <button
          type="button"
          onClick={() => rotateCanvas(90)}
          disabled={loading || error || saving}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white/80 hover:bg-white/20 disabled:opacity-40"
          aria-label="Rotate 90°"
          title="Rotate 90°"
        >
          <ToolIcon id="rotate90" />
        </button>
        <button
          type="button"
          onClick={flipHorizontal}
          disabled={loading || error || saving}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white/80 hover:bg-white/20 disabled:opacity-40"
          aria-label="Flip horizontal"
          title="Flip horizontal"
        >
          <ToolIcon id="flipHorizontal" />
        </button>
        <div className="w-px h-5 bg-white/15 mx-0.5" aria-hidden />
        <button
          type="button"
          onClick={undo}
          disabled={!canUndo || saving}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white/80 hover:bg-white/20 disabled:opacity-40"
          aria-label="Undo"
          title="Undo"
        >
          <ToolIcon id="undo" />
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={!canRedo || saving}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 text-white/80 hover:bg-white/20 disabled:opacity-40"
          aria-label="Redo"
          title="Redo"
        >
          <ToolIcon id="redo" />
        </button>
        <p className="text-[10px] text-white/45 ml-auto hidden sm:block">
          {hintByTool[tool]}
        </p>
      </div>

      {textDraft && (
        <div
          data-text-style-bar
          className="flex flex-wrap items-center justify-center gap-3 px-4 py-2 border-b border-white/10 shrink-0 bg-black/40"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-1.5">
            {TEXT_COLORS.map((swatch) => {
              const active = (textDraft.color || textColor) === swatch.value;
              return (
                <button
                  key={swatch.id}
                  type="button"
                  title={swatch.label}
                  aria-label={swatch.label}
                  aria-pressed={active}
                  onClick={() => updateTextStyle({ color: swatch.value })}
                  className={`w-5 h-5 rounded-full border-2 transition-transform ${
                    active ? 'border-white scale-110' : 'border-white/30 hover:border-white/70'
                  }`}
                  style={{ backgroundColor: swatch.value }}
                />
              );
            })}
          </div>
          <div className="w-px h-5 bg-white/15 hidden sm:block" aria-hidden />
          <div className="flex items-center gap-1">
            {TEXT_SIZES.map((sizeOption) => {
              const active = (textDraft.size || textSize) === sizeOption.id;
              return (
                <button
                  key={sizeOption.id}
                  type="button"
                  title={sizeOption.label}
                  aria-label={sizeOption.label}
                  aria-pressed={active}
                  onClick={() => updateTextStyle({ size: sizeOption.id })}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                    active
                      ? 'bg-white text-gray-900'
                      : 'bg-white/10 text-white/80 hover:bg-white/20'
                  }`}
                >
                  {sizeOption.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={commitText}
            className="px-3 py-1 rounded-md text-[11px] font-semibold bg-primary-600 hover:bg-primary-700 text-white"
          >
            Add text
          </button>
        </div>
      )}

      <div className="relative flex-1 min-h-0 flex items-center justify-center p-4 overflow-hidden">
        {loading && <p className="text-sm text-white/50">Loading image…</p>}
        {error && <p className="text-sm text-red-300">{error}</p>}
        <div className={`relative max-w-full max-h-full ${loading || error ? 'hidden' : ''}`}>
          <canvas
            ref={canvasRef}
            className="max-w-full max-h-[calc(100vh-10rem)] touch-none cursor-crosshair bg-black/40 rounded-lg shadow-2xl"
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
          />
          {dragRect && canvasRef.current && (
            <div
              className={`absolute border-2 pointer-events-none ${
                tool === 'circle' ? 'rounded-full' : ''
              } ${previewBorderClass}`}
              style={{
                left: `${(dragRect.x / canvasRef.current.width) * 100}%`,
                top: `${(dragRect.y / canvasRef.current.height) * 100}%`,
                width: `${(dragRect.w / canvasRef.current.width) * 100}%`,
                height: `${(dragRect.h / canvasRef.current.height) * 100}%`,
              }}
            />
          )}
          {textDraft && canvasRef.current && (
            <>
              {textDraft.mode === 'arrowText' && (
                <>
                  <div
                    className="absolute w-2.5 h-2.5 -ml-1.5 -mt-1.5 rounded-full border border-white pointer-events-none shadow"
                    style={{
                      left: `${(textDraft.targetX / canvasRef.current.width) * 100}%`,
                      top: `${(textDraft.targetY / canvasRef.current.height) * 100}%`,
                      backgroundColor: textDraft.color || textColor || '#ef4444',
                    }}
                  />
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                    viewBox={`0 0 ${canvasRef.current.width} ${canvasRef.current.height}`}
                    preserveAspectRatio="none"
                  >
                    <line
                      x1={textDraft.labelX}
                      y1={textDraft.labelY}
                      x2={textDraft.targetX}
                      y2={textDraft.targetY}
                      stroke={textDraft.color || textColor || '#ef4444'}
                      strokeWidth={Math.max(2, canvasRef.current.width * 0.0025)}
                      strokeDasharray={`${Math.max(6, canvasRef.current.width * 0.006)} ${Math.max(4, canvasRef.current.width * 0.004)}`}
                    />
                  </svg>
                </>
              )}
              <textarea
                autoFocus
                rows={3}
                value={textDraft.value}
                onChange={(e) => setTextDraft((prev) => ({ ...prev, value: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    commitText();
                  }
                  if (e.key === 'Escape') setTextDraft(null);
                }}
                onBlur={(e) => {
                  // Keep editing when clicking the top style bar / Add text.
                  const next = e.relatedTarget;
                  if (next && typeof next.closest === 'function' && next.closest('[data-text-style-bar]')) {
                    return;
                  }
                  commitText();
                }}
                className="absolute z-10 px-2.5 py-2 font-semibold bg-black/80 border border-white/20 rounded-lg outline-none shadow-xl resize-y min-w-[14rem] w-56 max-w-[70vw]"
                style={{
                  left: Math.max(0, Math.min(textDraft.left, (canvasRef.current?.clientWidth || 0) - 224)),
                  top: Math.max(0, textDraft.top),
                  color: textDraft.color || textColor,
                  fontSize:
                    (textDraft.size || textSize) === 'small'
                      ? 13
                      : (textDraft.size || textSize) === 'big'
                        ? 18
                        : 15,
                  lineHeight: 1.35,
                }}
                placeholder="Type your note…"
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageAnnotatorModal;
